import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import { useHistory, useLocation } from "react-router-dom";
import { collection, onSnapshot, doc, setDoc, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import axios from "axios";
import { db, auth } from "../../firebase";
import { getIdToken, applyActionCode } from "firebase/auth";
import { useFirebaseAuth } from "../../contexts/FirebaseAuthContext";
import useLogin from "../../utils/useLogin";
import StoreContext from "../../components/Store/context";
import UILogo from "../../components/UI/Logo/UILogo";
import { fetchCnpjInfo } from "../../utils/brasilApi";
import { getHubApiUrl } from "../../utils/hubApi";
import SubscriptionCheckoutModal from "../../components/UI/Subscription/SubscriptionCheckoutModal";
import "./Onboarding.css";


const segmentsList = [
  "Restaurante / Alimentação",
  "Roupas / Moda",
  "Cosméticos / Beleza",
  "Mercado / Conveniência",
  "Eletrônicos / Tecnologia",
  "Serviços Gerais",
  "Outro",
];

const mapCnaeToSegment = (cnaeDesc) => {
  if (!cnaeDesc) return "";
  const desc = cnaeDesc.toLowerCase();
  if (desc.includes("restaurante") || desc.includes("alimento") || desc.includes("refeic") || desc.includes("bar") || desc.includes("lanchonete") || desc.includes("padaria") || desc.includes("pizzaria") || desc.includes("bebida")) {
    return "Restaurante / Alimentação";
  }
  if (desc.includes("vestuario") || desc.includes("roupa") || desc.includes("calcado") || desc.includes("moda") || desc.includes("confeccao") || desc.includes("tecido") || desc.includes("bolsa") || desc.includes("acessorio")) {
    return "Roupas / Moda";
  }
  if (desc.includes("beleza") || desc.includes("estetica") || desc.includes("cosmet") || desc.includes("salao") || desc.includes("cabelereir") || desc.includes("barbearia") || desc.includes("perfum")) {
    return "Cosméticos / Beleza";
  }
  if (desc.includes("mercado") || desc.includes("mercearia") || desc.includes("supermercado") || desc.includes("minimercado") || desc.includes("conveniencia") || desc.includes("hortifrut")) {
    return "Mercado / Conveniência";
  }
  if (desc.includes("informatica") || desc.includes("computador") || desc.includes("eletron") || desc.includes("telecom") || desc.includes("celular") || desc.includes("software") || desc.includes("equipamento")) {
    return "Eletrônicos / Tecnologia";
  }
  if (desc.includes("servico") || desc.includes("mecanic") || desc.includes("oficina") || desc.includes("manutencao") || desc.includes("consultoria") || desc.includes("limpeza") || desc.includes("auto")) {
    return "Serviços Gerais";
  }
  return "Outro";
};

const mapNichoToSegment = (nicho) => {
  if (!nicho) return "";
  const n = nicho.toLowerCase().trim();
  if (["beauty", "beleza", "salao", "saloes", "estetica"].includes(n)) return "Cosméticos / Beleza";
  if (["restaurante", "restaurantes", "bares", "bar", "lanchonete", "alimentacao"].includes(n)) return "Restaurante / Alimentação";
  if (["oficina", "oficinas", "mecanica", "servicos", "servico"].includes(n)) return "Serviços Gerais";
  if (["moda", "roupas", "roupa", "vestuario"].includes(n)) return "Roupas / Moda";
  if (["mercado", "conveniencia", "mercearia", "supermercado"].includes(n)) return "Mercado / Conveniência";
  if (["tecnologia", "tech", "eletronicos", "informatica"].includes(n)) return "Eletrônicos / Tecnologia";
  if (["varejo", "loja", "comercio"].includes(n)) return "Roupas / Moda";
  return "Outro";
};

const getWebmailInfo = (email) => {
  if (!email) return null;
  const domain = email.split("@")[1]?.toLowerCase();
  if (domain === "gmail.com" || domain === "googlemail.com") {
    return { name: "Abrir Gmail", url: "https://mail.google.com" };
  }
  if (["outlook.com", "hotmail.com", "live.com", "msn.com"].includes(domain)) {
    return { name: "Abrir Outlook", url: "https://outlook.live.com" };
  }
  if (["yahoo.com", "yahoo.com.br"].includes(domain)) {
    return { name: "Abrir Yahoo Mail", url: "https://mail.yahoo.com" };
  }
  if (domain === "icloud.com") {
    return { name: "Abrir iCloud Mail", url: "https://www.icloud.com/mail" };
  }
  return null;
};

export default function Onboarding() {
  const history = useHistory();
  const location = useLocation();
  const { setCredencials } = useContext(StoreContext);
  const { currentUser, loginWithEmail, registerWithEmail, loginWithGoogle, resetPassword, logout, sendVerificationEmail, reloadUser } = useFirebaseAuth();

  const searchParams = new URLSearchParams(location.search);
  const planFromUrl = searchParams.get("plan");
  const nichoFromUrl = searchParams.get("nicho") || searchParams.get("segment");

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const processingRef = useRef(false);

  // Auth Modal State
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authPasswordConfirm, setAuthPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginView, setIsLoginView] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authSuccessMsg, setAuthSuccessMsg] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Email Verification Modal State
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [emailVerifiedSuccess, setEmailVerifiedSuccess] = useState(false);

  // Cooldown countdown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Handle Firebase Action Code (deep link confirmation)
  useEffect(() => {
    const mode = searchParams.get("mode");
    const oobCode = searchParams.get("oobCode");
    if (mode === "verifyEmail" && oobCode) {
      applyActionCode(auth, oobCode)
        .then(async () => {
          setEmailVerifiedSuccess(true);
          if (auth.currentUser) await auth.currentUser.reload();
          if (reloadUser) await reloadUser();
          setTimeout(() => {
            setShowEmailVerificationModal(false);
            processCompleteOnboarding(auth.currentUser);
          }, 1200);
        })
        .catch((err) => {
          console.warn("Error applying action code:", err);
          setVerificationError("O link de verificação expirou ou já foi utilizado.");
        });
    }
  }, [location.search]);

  // Auto-show verification modal when user is unverified
  useEffect(() => {
    const user = currentUser || auth.currentUser;
    if (user && !user.emailVerified) {
      setShowEmailVerificationModal(true);
    }
  }, [currentUser]);

  // Real-time auto-polling for email verification status when modal is open
  useEffect(() => {
    let interval;
    if (showEmailVerificationModal && auth.currentUser && !emailVerifiedSuccess) {
      interval = setInterval(async () => {
        try {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            setEmailVerifiedSuccess(true);
            if (reloadUser) await reloadUser();
            setTimeout(() => {
              setShowEmailVerificationModal(false);
              processCompleteOnboarding(auth.currentUser);
            }, 1200);
          }
        } catch (e) {
          console.error("Auto-polling error:", e);
        }
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [showEmailVerificationModal, emailVerifiedSuccess]);

  // Form State
  const [businessForm, setBusinessForm] = useState(() => {
    const saved = localStorage.getItem("pendingBusinessData");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (!parsed.segment && nichoFromUrl) {
          parsed.segment = mapNichoToSegment(nichoFromUrl);
        }
        return parsed;
      } catch (e) { }
    }
    return {
      name: "",
      cpf_cnpj: "",
      segment: mapNichoToSegment(nichoFromUrl),
      instagram: "",
      whatsapp: "",
    };
  });

  // Plans & Addons State
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [preSelectedPlan, setPreSelectedPlan] = useState(false);
  const [addons, setAddons] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellDismissed, setUpsellDismissed] = useState(false);
  const [createdAccountId, setCreatedAccountId] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [userTrialUsed, setUserTrialUsed] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjFeedback, setCnpjFeedback] = useState("");
  const lastSearchedCnpjRef = useRef("");

  // Detect if user has already used trial limit
  useEffect(() => {
    const activeUser = currentUser || auth.currentUser;
    if (!activeUser) {
      setUserTrialUsed(false);
      return;
    }

    const unsub = onSnapshot(doc(db, "users", activeUser.uid), (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        if (userData.trial_used === true && userData.role !== "super_admin" && userData.role !== "reseller") {
          setUserTrialUsed(true);
        }
      }
    });

    return () => unsub();
  }, [currentUser]);

  // Session ID & Auto-Save Lead Draft for Abandoned Cart Tracking
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem("onboarding_session_id");
    if (!sid) {
      sid = "sess_" + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
      localStorage.setItem("onboarding_session_id", sid);
    }
    return sid;
  });

  const saveLeadDraft = async (data = businessForm, plan = selectedPlan, currentStep = step, status = "onboarding_incomplete", accId = createdAccountId) => {
    if (!data?.name && !data?.whatsapp && !data?.cpf_cnpj) return;
    try {
      const leadRef = doc(db, "onboarding_leads", sessionId);
      await setDoc(leadRef, {
        session_id: sessionId,
        name: data.name || "",
        whatsapp: data.whatsapp || "",
        cpf_cnpj: data.cpf_cnpj || "",
        segment: data.segment || "",
        selected_plan: plan || null,
        step_reached: currentStep,
        status: status,
        user_id: currentUser?.uid || auth.currentUser?.uid || null,
        user_email: currentUser?.email || auth.currentUser?.email || null,
        account_id: accId || null,
        updated_at: serverTimestamp()
      }, { merge: true });
    } catch (e) {
      console.warn("Failed to auto-save lead draft:", e);
    }
  };

  const { loginWithToken } = useLogin({
    onCompleted: () => {
      history.replace("/home");
    },
  });

  // Formatting helpers
  const formatPhone = (val) => {
    if (!val) return "";
    let v = val.replace(/\D/g, "");
    if (v.length > 11) v = v.slice(0, 11);
    if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    if (v.length > 10) v = `${v.slice(0, 10)}-${v.slice(10)}`;
    return v;
  };

  const formatCpfCnpj = (val) => {
    if (!val) return "";
    let v = val.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (v.length <= 11) {
      v = v.replace(/^([a-zA-Z0-9]{3})([a-zA-Z0-9])/, "$1.$2");
      v = v.replace(/^([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, "$1.$2.$3");
      v = v.replace(/([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})([a-zA-Z0-9]{1,2})$/, "$1.$2.$3-$4");
    } else {
      if (v.length > 14) v = v.slice(0, 14);
      v = v.replace(/^([a-zA-Z0-9]{2})([a-zA-Z0-9])/, "$1.$2");
      v = v.replace(/^([a-zA-Z0-9]{2})\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, "$1.$2.$3");
      v = v.replace(/\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, ".$1/$2");
      v = v.replace(/\/([a-zA-Z0-9]{4})([a-zA-Z0-9])/, "/$1-$2");
    }
    return v;
  };

  // CNPJ Lookup via BrasilAPI (with deduplication)
  const handleCnpjLookup = async (cnpjValue) => {
    const cleanCnpj = (cnpjValue || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (cleanCnpj.length !== 14 || lastSearchedCnpjRef.current === cleanCnpj || cnpjLoading) {
      return;
    }

    lastSearchedCnpjRef.current = cleanCnpj;
    setCnpjLoading(true);
    setCnpjFeedback("");

    try {
      const data = await fetchCnpjInfo(cleanCnpj);
      if (data) {
        const companyName = data.nome_fantasia || data.razao_social || "";
        const phone = data.ddd_telefone_1 || data.ddd_telefone_2 || "";
        const segment = mapCnaeToSegment(data.cnae_fiscal_descricao);

        setBusinessForm((prev) => {
          const updated = {
            ...prev,
            name: companyName || prev.name,
            whatsapp: prev.whatsapp ? prev.whatsapp : (phone ? formatPhone(phone) : ""),
            segment: segment || prev.segment || "",
          };
          localStorage.setItem("pendingBusinessData", JSON.stringify(updated));
          return updated;
        });

        setCnpjFeedback(`✓ Dados carregados: ${data.razao_social || companyName}`);
      }
    } catch (err) {
      console.warn("CNPJ lookup failed:", err);
    } finally {
      setCnpjLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    let formattedVal = value;
    if (field === "whatsapp") formattedVal = formatPhone(value);
    if (field === "cpf_cnpj") {
      formattedVal = formatCpfCnpj(value);
      const clean = formattedVal.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (clean.length === 14) {
        handleCnpjLookup(clean);
      } else {
        lastSearchedCnpjRef.current = "";
        if (cnpjFeedback) setCnpjFeedback("");
      }
    }

    setBusinessForm((prev) => {
      const updated = { ...prev, [field]: formattedVal };
      localStorage.setItem("pendingBusinessData", JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      saveLeadDraft(businessForm, selectedPlan, step);
    }, 1500);
    return () => clearTimeout(timer);
  }, [businessForm, selectedPlan, step]);

  // Fetch plans & addons
  useEffect(() => {
    const unsubPlans = onSnapshot(collection(db, "plans"), (snap) => {
      let fetchedPlans = [];
      if (!snap.empty) {
        fetchedPlans = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.is_active !== false);
      } else {
        fetchedPlans = [];
      }
      setPlans(fetchedPlans);

      // Dynamic plan matching against URL param
      if (planFromUrl) {
        const matchingPlan = fetchedPlans.find(
          (p) => String(p.id).toLowerCase() === String(planFromUrl).toLowerCase() ||
            (p.name && String(p.name).toLowerCase() === String(planFromUrl).toLowerCase())
        );
        if (matchingPlan) {
          setSelectedPlan(matchingPlan.id);
          setPreSelectedPlan(true);
        } else {
          // No match: follow normal flow without pre-selected plan
          setPreSelectedPlan(false);
          if (!selectedPlan && fetchedPlans.length > 0) {
            const defaultP = fetchedPlans.find((p) => p.is_highlight) || fetchedPlans[0];
            setSelectedPlan(defaultP.id);
          }
        }
      } else {
        setPreSelectedPlan(false);
        if (!selectedPlan && fetchedPlans.length > 0) {
          const defaultP = fetchedPlans.find((p) => p.is_highlight) || fetchedPlans[0];
          setSelectedPlan(defaultP.id);
        }
      }
    });

    const unsubAddons = onSnapshot(collection(db, "addons"), (snap) => {
      if (!snap.empty) {
        const activeAddons = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((a) => a.is_active !== false);
        setAddons(activeAddons);
      }
    });

    return () => {
      unsubPlans();
      unsubAddons();
    };
  }, [planFromUrl]);

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!businessForm.name || !businessForm.whatsapp || !businessForm.cpf_cnpj) {
      setError("Por favor, preencha todos os campos obrigatórios (*).");
      return;
    }
    setError("");
    saveLeadDraft(businessForm, selectedPlan, 1, "onboarding_incomplete");

    if (preSelectedPlan && selectedPlan) {
      if (!userTrialUsed && addons.length > 0 && selectedAddonIds.length === 0 && !upsellDismissed) {
        setShowUpsellModal(true);
        return;
      }
      handleStartTrialOrCheckout();
    } else {
      setStep(2);
    }
  };

  const handleOpenCheckout = async (userParam) => {
    const activeUser = userParam || currentUser || auth.currentUser;
    if (!activeUser) {
      setShowAuthModal(true);
      return;
    }

    try {
      setLoading(true);
      let accountId = createdAccountId;
      const cleanFormData = {
        name: businessForm.name,
        cpf_cnpj: businessForm.cpf_cnpj.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(),
        segment: businessForm.segment || "Geral",
        whatsapp: businessForm.whatsapp.replace(/\D/g, ""),
        instagram: businessForm.instagram || "",
      };

      if (!accountId) {
        const accountData = {
          ...cleanFormData,
          owner_user_id: activeUser.uid,
          status: "pending_payment",
          trial_eligible: false,
          created_at: serverTimestamp(),
        };
        const docRef = await addDoc(collection(db, "accounts"), accountData);
        accountId = docRef.id;
        setCreatedAccountId(accountId);
      }

      setShowCheckoutModal(true);
    } catch (err) {
      console.error("Error preparing account for checkout:", err);
      setError("Erro ao preparar conta para contratação: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrialOrCheckout = async () => {
    const activeUser = currentUser || auth.currentUser;
    if (!activeUser) {
      setShowAuthModal(true);
      return;
    }

    try {
      await activeUser.reload();
    } catch (e) { }

    if (!activeUser.emailVerified) {
      setShowEmailVerificationModal(true);
      return;
    }

    if (userTrialUsed) {
      await handleOpenCheckout(activeUser);
      return;
    }

    await processCompleteOnboarding(activeUser);
  };

  const handleStartTrial = handleStartTrialOrCheckout;

  const processCompleteOnboarding = async (userParam) => {
    if (processingRef.current) return;
    processingRef.current = true;

    setLoading(true);
    setError("");

    try {
      const activeUser = userParam || currentUser || auth.currentUser;
      if (!activeUser) {
        throw new Error("Usuário não autenticado. Por favor, tente novamente.");
      }

      let accountId = createdAccountId;
      const cleanFormData = {
        name: businessForm.name,
        cpf_cnpj: businessForm.cpf_cnpj.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(),
        segment: businessForm.segment || "Geral",
        whatsapp: businessForm.whatsapp.replace(/\D/g, ""),
        instagram: businessForm.instagram || "",
      };

      if (!accountId) {
        const accountData = {
          ...cleanFormData,
          owner_user_id: activeUser.uid,
          status: "pending_payment",
          trial_eligible: !userTrialUsed,
          created_at: serverTimestamp(),
        };
        const docRef = await addDoc(collection(db, "accounts"), accountData);
        accountId = docRef.id;
        setCreatedAccountId(accountId);
      }

      let token;
      if (typeof activeUser.getIdToken === "function") {
        token = await activeUser.getIdToken(true);
      } else if (auth.currentUser && typeof auth.currentUser.getIdToken === "function") {
        token = await auth.currentUser.getIdToken(true);
      } else if (activeUser) {
        token = await getIdToken(activeUser, true);
      } else {
        token = await getIdToken(auth.currentUser, true);
      }

      const apiUrl = getHubApiUrl();

      // 1. Create trial subscription via backend API endpoint
      const res = await axios.post(
        `${apiUrl}/subscriptions/trial/create`,
        null,
        {
          params: {
            account_id: accountId,
            plan_id: selectedPlan || "pro",
            ...(selectedAddonIds.length > 0 ? { addon_ids: selectedAddonIds.join(",") } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      localStorage.removeItem("pendingBusinessData");

      // Auto-authenticate in Duzz-Commercial same tab
      let loginToken = res.data?.login_token;
      if (!loginToken && res.data?.system_url) {
        try {
          const urlObj = new URL(res.data.system_url);
          loginToken = urlObj.searchParams.get("loginToken");
        } catch (e) {
          console.error("Error parsing system_url token:", e);
        }
      }

      if (loginToken) {
        loginWithToken(loginToken);
      } else {
        const userAccess = {
          id: activeUser?.uid || "user",
          name: businessForm.name || "Admin",
          lastName: "",
          username: activeUser?.email || authEmail,
          email: activeUser?.email || authEmail,
          sessionToken: token || "local-token",
          company: accountId,
          companyData: {
            id: accountId,
            name: businessForm.name,
            cpf_cnpj: businessForm.cpf_cnpj,
            segment: businessForm.segment,
          },
          profile: {
            id: 3,
            name: "Administrador",
          },
          role: "admin",
        };
        setCredencials(userAccess);
        history.replace("/home");
      }
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || err.message;
      if (typeof detail === "string" && (detail.includes("teste grátis") || detail.includes("trial"))) {
        setUserTrialUsed(true);
        setError("");
        // Automatically open checkout modal for plan acquisition
        handleOpenCheckout(userParam || currentUser || auth.currentUser);
      } else {
        setError(
          "Falha ao concluir configuração: " + detail
        );
      }
    } finally {
      processingRef.current = false;
      setLoading(false);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (!authEmail || !authPassword || (!isLoginView && !authPasswordConfirm)) {
      setAuthError("Por favor, preencha todos os campos.");
      return;
    }
    if (!isLoginView && authPassword !== authPasswordConfirm) {
      setAuthError("As senhas não coincidem.");
      return;
    }
    if (!isLoginView && authPassword.length < 6) {
      setAuthError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    try {
      setAuthError("");
      setAuthSuccessMsg("");
      setAuthLoading(true);
      let userRes;
      if (isLoginView) {
        userRes = await loginWithEmail(authEmail, authPassword);
      } else {
        userRes = await registerWithEmail(authEmail, authPassword);
        setResendCooldown(60);
      }
      setShowAuthModal(false);

      const activeUser = userRes?.user || auth.currentUser;
      if (activeUser) {
        await activeUser.reload();
        if (!activeUser.emailVerified) {
          setShowEmailVerificationModal(true);
          return;
        }
      }

      await processCompleteOnboarding(activeUser);
    } catch (err) {
      let errorMsg = err.message;
      if (err.code === "auth/email-already-in-use")
        errorMsg = "Este e-mail já está em uso.";
      if (err.code === "auth/invalid-email") errorMsg = "E-mail inválido.";
      if (err.code === "auth/user-not-found")
        errorMsg = "Usuário não encontrado.";
      if (
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      )
        errorMsg = "E-mail ou senha incorretos.";
      if (err.code === "auth/weak-password")
        errorMsg = "A senha deve ter pelo menos 6 caracteres.";
      setAuthError(errorMsg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setAuthError("");
      setAuthLoading(true);
      const googleRes = await loginWithGoogle();
      setShowAuthModal(false);
      await processCompleteOnboarding(googleRes?.user);
    } catch (err) {
      console.error(err);
      setAuthError("Falha ao autenticar com o Google: " + (err.message || "Erro desconhecido."));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleCheckEmailVerified = async () => {
    setVerifyingEmail(true);
    setVerificationError("");
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (reloadUser) await reloadUser();
        if (auth.currentUser.emailVerified) {
          setShowEmailVerificationModal(false);
          await processCompleteOnboarding(auth.currentUser);
        } else {
          setVerificationError(
            `E-mail (${auth.currentUser.email}) ainda não verificado. Por favor, cheque sua caixa de entrada ou pasta de spam.`
          );
        }
      } else {
        setVerificationError("Sessão expirada. Por favor, faça login novamente.");
      }
    } catch (err) {
      setVerificationError("Erro ao verificar e-mail: " + err.message);
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;
    setVerificationError("");
    try {
      if (auth.currentUser) {
        await sendVerificationEmail(auth.currentUser);
        setResendCooldown(60);
      }
    } catch (err) {
      setVerificationError("Falha ao reenviar e-mail: " + err.message);
    }
  };

  const selectedPlanObj = plans.find((p) => p.id === selectedPlan) || plans[0];
  const trialDays = selectedPlanObj?.trial_days ?? selectedPlanObj?.trialDays ?? selectedPlanObj?.trial_period_days ?? 7;

  const planFreq = String(selectedPlanObj?.frequency || "MONTHLY").toUpperCase();
  const isPlanYearly = planFreq === "YEARLY" || planFreq === "ANNUAL" || planFreq === "YEAR";
  const basePlanAmount = Number(selectedPlanObj?.amount || 0);

  const selectedAddonsList = addons.filter((a) => selectedAddonIds.includes(a.id));

  let totalAnnual = isPlanYearly ? basePlanAmount : basePlanAmount * 12;
  let totalMonthly = isPlanYearly ? basePlanAmount / 12 : basePlanAmount;

  selectedAddonsList.forEach((addon) => {
    const addonAmt = Number(addon.amount || 0);
    const addonFreq = String(addon.frequency || "MONTHLY").toUpperCase();
    const isAddonYearly = addonFreq === "YEARLY" || addonFreq === "ANNUAL" || addonFreq === "YEAR";

    if (isAddonYearly) {
      totalAnnual += addonAmt;
      totalMonthly += addonAmt / 12;
    } else {
      totalAnnual += addonAmt * 12;
      totalMonthly += addonAmt;
    }
  });

  return (
    <div className="onboarding-global">
      <div className="onboarding-header">
        <Container maxWidth="md">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <img
              src="/image/Alinhado.png"
              alt="Duzz"
              className="onboarding-header-logo"
              onClick={() => history.push("/")}
            />
            {currentUser ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" style={{ color: "#17497F", fontWeight: 600 }}>
                  👤 {currentUser.email}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    setCredencials(null);
                    await logout();
                    setCreatedAccountId(null);
                    setAuthEmail("");
                    setAuthPassword("");
                    setAuthPasswordConfirm("");
                    setIsLoginView(false);
                    setShowAuthModal(true);
                  }}
                  style={{ fontWeight: 600, textTransform: "none", borderRadius: "8px", marginLeft: "12px" }}
                >
                  Trocar de conta
                </Button>
              </Box>
            ) : (
              <Button
                variant="text"
                color="primary"
                onClick={() => history.push("/")}
                style={{ fontWeight: 600, textTransform: "none" }}
              >
                Já possui conta? <strong>Entrar</strong>
              </Button>
            )}
          </Box>
        </Container>
      </div>

      <Container maxWidth="md">
        <Paper className="onboarding-card" elevation={0}>
          {/* Stepper Progress Bar Header */}
          <div className="onboarding-progress-bar">
            {preSelectedPlan ? (
              <>
                <div
                  className="onboarding-step-indicator"
                  style={{ cursor: "pointer" }}
                  onClick={() => setStep(2)}
                  title="Clique para ver ou alterar o plano"
                >
                  <div className={`step-circle ${step === 2 ? "active" : "completed"}`}>
                    {step === 2 ? "1" : <CheckCircleIcon style={{ fontSize: 20 }} />}
                  </div>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 700, color: step === 2 ? "#1B3A5C" : "#166534" }}
                  >
                    Plano {selectedPlanObj ? `(${selectedPlanObj.name || selectedPlanObj.reason})` : ""}
                  </Typography>
                </div>

                <div className="step-line active" />

                <div
                  className="onboarding-step-indicator"
                  style={{ cursor: "pointer" }}
                  onClick={() => setStep(1)}
                >
                  <div className={`step-circle ${step === 1 ? "active" : "inactive"}`}>
                    2
                  </div>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 700, color: step === 1 ? "#1B3A5C" : "#6B7280" }}
                  >
                    Dados do Negócio
                  </Typography>
                </div>
              </>
            ) : (
              <>
                <div className="onboarding-step-indicator">
                  <div className={`step-circle ${step >= 1 ? (step > 1 ? "completed" : "active") : "inactive"}`}>
                    {step > 1 ? <CheckCircleIcon style={{ fontSize: 20 }} /> : "1"}
                  </div>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: step === 1 ? "#1B3A5C" : "#6B7280" }}>
                    Dados do Negócio
                  </Typography>
                </div>

                <div className={`step-line ${step >= 2 ? "active" : ""}`} />

                <div className="onboarding-step-indicator">
                  <div className={`step-circle ${step === 2 ? "active" : "inactive"}`}>
                    2
                  </div>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: step === 2 ? "#1B3A5C" : "#6B7280" }}>
                    {userTrialUsed ? "Plano de Assinatura" : "Plano & Teste Grátis"}
                  </Typography>
                </div>
              </>
            )}
          </div>

          {error && (
            <Box mb={3} p={2} bgcolor="#FEF2F2" border="1px solid #FCA5A5" borderRadius="10px">
              <Typography color="error" variant="body2" style={{ fontWeight: 600 }}>
                ⚠️ {error}
              </Typography>
              {userTrialUsed && (
                <Box mt={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleOpenCheckout()}
                    style={{
                      backgroundColor: "#17497F",
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 8,
                    }}
                  >
                    💳 Prosseguir para Contratação do Plano →
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {step === 1 && (
            <form onSubmit={handleStep1Submit}>
              {preSelectedPlan && selectedPlanObj && (
                <Box
                  mb={3}
                  p={2}
                  bgcolor="#F0F7FF"
                  border="1px solid #BFDBFE"
                  borderRadius="12px"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" style={{ color: "#1E40AF", fontWeight: 700 }}>
                      🎯 Plano Selecionado: <strong>{selectedPlanObj.name || selectedPlanObj.reason}</strong>
                      <span style={{ color: "#3B82F6", fontWeight: 600, marginLeft: 6 }}>
                        (R$ {Number(selectedPlanObj.amount || 0).toFixed(2).replace(".", ",")}/{String(selectedPlanObj.frequency || "MONTHLY").toUpperCase() === "YEARLY" ? "ano" : "mês"})
                      </span>
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => setStep(2)}
                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 8, fontSize: "12px" }}
                  >
                    Alterar plano
                  </Button>
                </Box>
              )}

              <Typography variant="h5" style={{ fontWeight: 800, marginBottom: "6px", color: "#1B3A5C" }}>
                Cadastre sua Empresa
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: "28px" }}>
                Preencha os dados do seu negócio para iniciar seu teste grátis sem compromisso.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="CPF ou CNPJ *"
                    variant="outlined"
                    value={businessForm.cpf_cnpj}
                    onChange={(e) => handleInputChange("cpf_cnpj", e.target.value)}
                    onBlur={(e) => {
                      const clean = (e.target.value || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
                      if (clean.length === 14 && !cnpjLoading) {
                        handleCnpjLookup(clean);
                      }
                    }}
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    required
                    helperText={
                      cnpjLoading ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#17497F", fontWeight: 600 }}>
                          <CircularProgress size={12} color="inherit" /> Consultando Receita Federal...
                        </span>
                      ) : cnpjFeedback ? (
                        <span style={{ color: "#166534", fontWeight: 600 }}>{cnpjFeedback}</span>
                      ) : (
                        "Informe CNPJ para preenchimento automático"
                      )
                    }
                    InputProps={{
                      endAdornment: cnpjLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} color="primary" />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome da Empresa / Negócio *"
                    variant="outlined"
                    value={businessForm.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Ex: Minha Loja ou Serviços"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp *"
                    variant="outlined"
                    value={businessForm.whatsapp}
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                    placeholder="(00) 90000-0000"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Segmento do Negócio"
                    variant="outlined"
                    value={businessForm.segment}
                    onChange={(e) => handleInputChange("segment", e.target.value)}
                  >
                    {segmentsList.map((seg) => (
                      <MenuItem key={seg} value={seg}>
                        {seg}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instagram (opcional)"
                    variant="outlined"
                    value={businessForm.instagram}
                    onChange={(e) => handleInputChange("instagram", e.target.value)}
                    placeholder="@sualoja"
                  />
                </Grid>
              </Grid>

              {/* Seção de Upsell de Add-ons quando o plano veio pré-selecionado */}
              {preSelectedPlan && addons.length > 0 && (
                <Box mt={4} mb={2} p={3} bgcolor="#F8FAFC" borderRadius="14px" border="1px solid #E2E8F0">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="subtitle1" style={{ fontWeight: 800, color: "#17497F", display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: "18px" }}>✨</span> Módulos Adicionais (Add-ons)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Potencialize suas vendas incluindo recursos extras no seu teste grátis.
                      </Typography>
                    </Box>
                    {selectedAddonIds.length > 0 && (
                      <Chip
                        size="small"
                        label={`${selectedAddonIds.length} selecionado(s)`}
                        style={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", fontWeight: 700, border: "1px solid #BFDBFE" }}
                      />
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {addons.map((addon) => {
                      const isSelected = selectedAddonIds.includes(addon.id);
                      const addonFreq = String(addon.frequency || "MONTHLY").toUpperCase();
                      const isAddonYearly = addonFreq === "YEARLY" || addonFreq === "ANNUAL" || addonFreq === "YEAR";
                      const addonAmountFormatted = Number(addon.amount || 0).toFixed(2).replace(".", ",");

                      return (
                        <Grid item xs={12} key={addon.id}>
                          <div
                            className={`addon-upsell-card ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedAddonIds(selectedAddonIds.filter((id) => id !== addon.id));
                              } else {
                                setSelectedAddonIds([...selectedAddonIds, addon.id]);
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => { }}
                                color="primary"
                                style={{ padding: 4 }}
                              />
                              <Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1E293B" }}>
                                    {addon.name || addon.reason}
                                  </Typography>
                                  {isSelected && (
                                    <span className="addon-badge-included">Incluído no Teste</span>
                                  )}
                                </Box>
                                {addon.description && (
                                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                                    {addon.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            <Box textAlign="right" style={{ minWidth: 100 }}>
                              <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#17497F" }}>
                                + R$ {addonAmountFormatted}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                /{isAddonYearly ? "ano" : "mês"}
                              </Typography>
                            </Box>
                          </div>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Resumo da Assinatura no Step 1 */}
                  <Divider style={{ margin: "18px 0 14px 0" }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 700, color: "#1E293B" }}>
                        Valor Total Estimado:
                      </Typography>
                      {trialDays > 0 && !userTrialUsed ? (
                        <Typography variant="caption" style={{ color: "#166534", fontWeight: 600, display: "block" }}>
                          ✓ {trialDays} dias de teste grátis (sem cobrança imediata)
                        </Typography>
                      ) : (
                        <Typography variant="caption" style={{ color: "#1E40AF", fontWeight: 600, display: "block" }}>
                          ✓ Cobrança recorrente {isPlanYearly ? "/ano" : "/mês"}
                        </Typography>
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6" style={{ fontWeight: 800, color: "#17497F" }}>
                        R$ {(isPlanYearly ? totalAnnual : totalMonthly).toFixed(2).replace(".", ",")}
                        <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 4, fontWeight: 700 }}>
                          {isPlanYearly ? "/ano" : "/mês"}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  className="btn-primary-cta"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : preSelectedPlan ? (
                    userTrialUsed ? (
                      "CONTINUAR PARA PAGAMENTO →"
                    ) : trialDays > 0 ? (
                      `INICIAR TESTE GRÁTIS (${trialDays} DIAS)`
                    ) : (
                      "CRIAR CONTA & CONTINUAR"
                    )
                  ) : (
                    "Continuar para Escolha do Plano →"
                  )}
                </Button>
              </Box>
            </form>
          )}

          {step === 2 && (() => {
            const currentPlanObj = plans.find((p) => p.id === selectedPlan) || plans[0];
            const trialDays = currentPlanObj?.trial_days ?? currentPlanObj?.trialDays ?? currentPlanObj?.trial_period_days ?? 7;

            const planFreq = String(currentPlanObj?.frequency || "MONTHLY").toUpperCase();
            const isPlanYearly = planFreq === "YEARLY" || planFreq === "ANNUAL" || planFreq === "YEAR";
            const basePlanAmount = Number(currentPlanObj?.amount || 0);

            const selectedAddonsList = addons.filter((a) => selectedAddonIds.includes(a.id));

            let totalAnnual = isPlanYearly ? basePlanAmount : basePlanAmount * 12;
            let totalMonthly = isPlanYearly ? basePlanAmount / 12 : basePlanAmount;

            selectedAddonsList.forEach((addon) => {
              const addonAmt = Number(addon.amount || 0);
              const addonFreq = String(addon.frequency || "MONTHLY").toUpperCase();
              const isAddonYearly = addonFreq === "YEARLY" || addonFreq === "ANNUAL" || addonFreq === "YEAR";

              if (isAddonYearly) {
                totalAnnual += addonAmt;
                totalMonthly += addonAmt / 12;
              } else {
                totalAnnual += addonAmt * 12;
                totalMonthly += addonAmt;
              }
            });

            return (
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <IconButton onClick={() => setStep(1)} size="small" style={{ marginRight: 8 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h5" style={{ fontWeight: 800, color: "#17497F" }}>
                    Escolha o Plano Ideal
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: "20px" }}>
                  {userTrialUsed
                    ? "Você já utilizou o teste grátis disponível para o seu usuário. Selecione um plano de assinatura para ativar seu novo negócio."
                    : trialDays > 0
                    ? `Você terá ${trialDays} dias de teste totalmente grátis. Cancele quando quiser.`
                    : "Escolha seu plano e comece a usar agora mesmo."}
                </Typography>

                {currentUser && (
                  <div className="connected-user-banner">
                    <Typography variant="body2" style={{ color: "#166534", fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>👤</span> Conectado como: <strong>{currentUser.email}</strong>
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setShowAuthModal(true)}
                      style={{ fontWeight: 600, textTransform: "none" }}
                    >
                      Trocar conta
                    </Button>
                  </div>
                )}

                <Grid container spacing={3} style={{ marginTop: "12px" }} alignItems="stretch">
                  {plans.map((plan) => {
                    const isSelected = selectedPlan === plan.id;
                    const planTrial = plan.trial_days ?? plan.trialDays ?? plan.trial_period_days ?? 7;
                    const pFreq = String(plan.frequency || "MONTHLY").toUpperCase();
                    const isPYearly = pFreq === "YEARLY" || pFreq === "ANNUAL" || pFreq === "YEAR";
                    const periodSuffix = isPYearly ? "/ano" : "/mês";
                    const monthlyEquiv = isPYearly ? Number(plan.amount) / 12 : Number(plan.amount);

                    return (
                      <Grid item xs={12} sm={6} key={plan.id} style={{ display: "flex", flexDirection: "column" }}>
                        <Card
                          className={`plan-card ${isSelected ? "selected" : ""}`}
                          variant="outlined"
                          onClick={() => setSelectedPlan(plan.id)}
                        >
                          {plan.is_highlight && (
                            <div className="plan-card-popular-badge">
                              Mais Popular
                            </div>
                          )}
                          <CardContent style={{ padding: "24px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <Box>
                              <Typography variant="h6" style={{ fontWeight: 800, color: "#17497F", paddingRight: plan.is_highlight ? "85px" : "0" }}>
                                {plan.name || plan.reason}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" style={{ margin: "12px 0 16px 0", lineHeight: 1.5 }}>
                                {plan.description}
                              </Typography>
                            </Box>

                            <Box mt="auto">
                              <Typography variant="h4" style={{ fontWeight: 800, color: "#17497F" }}>
                                R$ {Number(plan.amount).toFixed(2).replace(".", ",")}
                                <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 4, fontWeight: 700 }}>
                                  {periodSuffix}
                                </Typography>
                              </Typography>
                              {isPYearly && (
                                <Typography variant="caption" style={{ color: "#64748B", display: "block", marginTop: 2, fontWeight: 600 }}>
                                  (equiv. R$ {monthlyEquiv.toFixed(2).replace(".", ",")} / mês)
                                </Typography>
                              )}
                              {!userTrialUsed && planTrial > 0 && (
                                <Chip
                                  label={`${planTrial} dias grátis`}
                                  style={{
                                    marginTop: 10,
                                    fontWeight: 700,
                                    backgroundColor: "#DCFCE7",
                                    color: "#15803D",
                                    borderRadius: 8,
                                  }}
                                  size="small"
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {addons.length > 0 && (
                  <Box mt={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: "8px", color: "#17497F" }}>
                      Recursos Adicionais
                    </Typography>
                    <Grid container spacing={1}>
                      {addons.map((addon) => {
                        const addonFreq = String(addon.frequency || "MONTHLY").toUpperCase();
                        const isAddonYearly = addonFreq === "YEARLY" || addonFreq === "ANNUAL" || addonFreq === "YEAR";
                        const addonAmountFormatted = Number(addon.amount || 0).toFixed(2).replace(".", ",");
                        const isChecked = selectedAddonIds.includes(addon.id);

                        return (
                          <Grid item xs={12} key={addon.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedAddonIds([...selectedAddonIds, addon.id]);
                                    } else {
                                      setSelectedAddonIds(selectedAddonIds.filter((id) => id !== addon.id));
                                    }
                                  }}
                                  color="primary"
                                />
                              }
                              label={
                                <Typography variant="body2" style={{ fontWeight: 600, color: "#1D3B44" }}>
                                  {addon.name}{" "}
                                  <span style={{ color: "#17497F", fontWeight: 700 }}>
                                    (+R$ {addonAmountFormatted}{isAddonYearly ? "/ano" : "/mês"})
                                  </span>
                                </Typography>
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {/* Resumo da Assinatura e Valor Final */}
                <Box mt={3} p={2.5} bgcolor="#F8FAFC" borderRadius="12px" border="1px solid #E2E8F0">
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#17497F", marginBottom: "12px" }}>
                    💳 Resumo do Valor Final:
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Plano Base ({currentPlanObj?.name || currentPlanObj?.reason || "Plano Selecionado"}):
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600, color: "#1E293B" }}>
                      R$ {basePlanAmount.toFixed(2).replace(".", ",")} {isPlanYearly ? "/ ano" : "/ mês"}
                    </Typography>
                  </Box>

                  {selectedAddonsList.map((addon) => {
                    const addonAmt = Number(addon.amount || 0);
                    const addonFreq = String(addon.frequency || "MONTHLY").toUpperCase();
                    const isAddonYearly = addonFreq === "YEARLY" || addonFreq === "ANNUAL" || addonFreq === "YEAR";

                    return (
                      <Box key={addon.id} display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="textSecondary">
                          + {addon.name}:
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600, color: "#17497F" }}>
                          + R$ {addonAmt.toFixed(2).replace(".", ",")} {isAddonYearly ? "/ ano" : "/ mês"}
                          {isPlanYearly && !isAddonYearly && (
                            <span style={{ color: "#64748B", fontWeight: 400, marginLeft: 4 }}>
                              (+R$ {(addonAmt * 12).toFixed(2).replace(".", ",")} / ano)
                            </span>
                          )}
                        </Typography>
                      </Box>
                    );
                  })}

                  <Divider style={{ margin: "14px 0" }} />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" style={{ fontWeight: 800, color: "#17497F" }}>
                        Valor Total Estimado:
                      </Typography>
                      {trialDays > 0 && !userTrialUsed ? (
                        <Typography variant="caption" style={{ color: "#166534", fontWeight: 600, display: "block" }}>
                          ✓ {trialDays} dias de teste grátis (sem cobrança imediata)
                        </Typography>
                      ) : (
                        <Typography variant="caption" style={{ color: "#1E40AF", fontWeight: 600, display: "block" }}>
                          ✓ Pagamento seguro via Asaas
                        </Typography>
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h5" style={{ fontWeight: 800, color: "#17497F" }}>
                        R$ {(isPlanYearly ? totalAnnual : totalMonthly).toFixed(2).replace(".", ",")}
                        <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 4, fontWeight: 700 }}>
                          {isPlanYearly ? "/ano" : "/mês"}
                        </Typography>
                      </Typography>
                      {isPlanYearly && (
                        <Typography variant="caption" style={{ color: "#64748B", fontWeight: 600, display: "block" }}>
                          (equiv. R$ {totalMonthly.toFixed(2).replace(".", ",")} / mês)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
                  <Button variant="outlined" onClick={() => setStep(1)} style={{ borderRadius: 10, textTransform: "none", fontWeight: 600 }}>
                    ← Voltar aos Dados da Empresa
                  </Button>

                  {preSelectedPlan ? (
                    <Button
                      variant="contained"
                      className="btn-primary-cta"
                      onClick={() => setStep(1)}
                    >
                      Avançar para Dados do Negócio →
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      className="btn-primary-cta"
                      disabled={loading}
                      onClick={handleStartTrialOrCheckout}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : userTrialUsed ? (
                        "PROSSEGUIR PARA PAGAMENTO →"
                      ) : trialDays > 0 ? (
                        `TESTAR GRÁTIS POR ${trialDays} DIAS`
                      ) : (
                        "ASSINAR AGORA"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Paper>

        {/* Modal de Upsell no Fechamento da Compra */}
        <Dialog
          open={showUpsellModal}
          onClose={() => setShowUpsellModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: { borderRadius: "16px", padding: "8px" },
          }}
        >
          <DialogTitle disableTypography style={{ padding: "20px 24px 8px 24px" }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <span style={{ fontSize: "24px" }}>✨</span>
              <Box>
                <Typography variant="h6" style={{ fontWeight: 800, color: "#17497F", lineHeight: 1.2 }}>
                  Turbine seu Plano com Recursos Extras!
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                  Aproveite para incluir módulos adicionais e testar 100% grátis durante seus {trialDays} dias.
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent style={{ padding: "16px 24px" }}>
            <Grid container spacing={2}>
              {addons.map((addon) => {
                const isSelected = selectedAddonIds.includes(addon.id);
                const addonFreq = String(addon.frequency || "MONTHLY").toUpperCase();
                const isAddonYearly = addonFreq === "YEARLY" || addonFreq === "ANNUAL" || addonFreq === "YEAR";
                const addonAmountFormatted = Number(addon.amount || 0).toFixed(2).replace(".", ",");

                return (
                  <Grid item xs={12} key={addon.id}>
                    <div
                      className={`addon-upsell-card ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedAddonIds(selectedAddonIds.filter((id) => id !== addon.id));
                        } else {
                          setSelectedAddonIds([...selectedAddonIds, addon.id]);
                        }
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={1.5}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => { }}
                          color="primary"
                          style={{ padding: 4 }}
                        />
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1E293B" }}>
                              {addon.name || addon.reason}
                            </Typography>
                            {isSelected && (
                              <span className="addon-badge-included">Incluído no Teste</span>
                            )}
                          </Box>
                          {addon.description && (
                            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                              {addon.description}
                            </Typography>
                          )}
                          {addon.features && Array.isArray(addon.features) && addon.features.length > 0 && (
                            <Box mt={1}>
                              {addon.features.map((feat, idx) => (
                                <Typography key={idx} variant="caption" style={{ display: "block", color: "#475569", fontSize: "11px" }}>
                                  ✓ {feat}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Box textAlign="right" style={{ minWidth: 100, flexShrink: 0 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#17497F" }}>
                          + R$ {addonAmountFormatted}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                          /{isAddonYearly ? "ano" : "mês"}
                        </Typography>
                      </Box>
                    </div>
                  </Grid>
                );
              })}
            </Grid>

            {/* Total estimado no modal */}
            <Box mt={2} p={2} bgcolor="#F8FAFC" borderRadius="10px" border="1px solid #E2E8F0" display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700, textTransform: "uppercase" }}>
                  Total Estimado após teste:
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: "#166534" }}>
                  ✓ R$ 0,00 cobrados hoje ({trialDays} dias grátis)
                </Typography>
              </Box>
              <Typography variant="h6" style={{ fontWeight: 800, color: "#17497F" }}>
                R$ {(isPlanYearly ? totalAnnual : totalMonthly).toFixed(2).replace(".", ",")}
                <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 3 }}>
                  {isPlanYearly ? "/ano" : "/mês"}
                </Typography>
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions style={{ padding: "16px 24px", justifyContent: "space-between" }}>
            <Button
              onClick={() => {
                setSelectedAddonIds([]);
                setShowUpsellModal(false);
                setUpsellDismissed(true);
                handleStartTrial();
              }}
              color="default"
              style={{ textTransform: "none", fontWeight: 600, color: "#64748B" }}
            >
              Continuar sem adicionais
            </Button>
            <Button
              variant="contained"
              className="btn-primary-cta"
              onClick={() => {
                setShowUpsellModal(false);
                setUpsellDismissed(true);
                handleStartTrial();
              }}
              style={{ fontSize: "14px", padding: "10px 22px" }}
            >
              {selectedAddonIds.length > 0 ? "Adicionar e Iniciar Teste →" : "Iniciar Teste Grátis →"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Auth Modal */}
        <Dialog open={showAuthModal} onClose={() => setShowAuthModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle style={{ textAlign: "center", fontWeight: 700 }}>
            {isLoginView ? "Entrar na sua conta" : "Criar sua conta de Administrador"}
          </DialogTitle>
          <form onSubmit={handleAuthSubmit}>
            <DialogContent>
              {currentUser && (
                <Box mb={2} p={1.5} bgcolor="#EFF6FF" borderRadius="8px" border="1px solid #BFDBFE" textAlign="center">
                  <Typography variant="caption" style={{ color: "#1E40AF", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Você está atualmente logado como <strong>{currentUser.email}</strong>
                  </Typography>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={async () => {
                      await logout();
                      setCreatedAccountId(null);
                      setAuthEmail("");
                      setAuthPassword("");
                      setIsLoginView(false);
                    }}
                    style={{ textTransform: "none", fontWeight: 700, fontSize: "12px" }}
                  >
                    🔓 Sair desta conta para cadastrar outra
                  </Button>
                </Box>
              )}

              {authError && (
                <Box mb={2} p={1} bgcolor="#ffebee" borderRadius="4px">
                  <Typography color="error" variant="caption">
                    ⚠️ {authError}
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleGoogleAuth}
                disabled={authLoading}
                style={{
                  borderColor: '#dadce0',
                  backgroundColor: '#ffffff',
                  color: '#3c4043',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  marginBottom: '16px',
                }}
                startIcon={
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.617z" />
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
                    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                  </svg>
                }
              >
                Continuar com o Google
              </Button>

              <Box display="flex" alignItems="center" my={2}>
                <Box flexGrow={1} height="1px" bgcolor="#e0e0e0" />
                <Typography variant="caption" color="textSecondary" style={{ padding: "0 12px", textTransform: "uppercase" }}>
                  ou com e-mail
                </Typography>
                <Box flexGrow={1} height="1px" bgcolor="#e0e0e0" />
              </Box>

              <TextField
                fullWidth
                label="Seu E-mail *"
                variant="outlined"
                margin="normal"
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Sua Senha *"
                variant="outlined"
                margin="normal"
                type={showPassword ? "text" : "password"}
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {!isLoginView && (
                <TextField
                  fullWidth
                  label="Confirme a Senha *"
                  variant="outlined"
                  margin="normal"
                  type={showPassword ? "text" : "password"}
                  value={authPasswordConfirm}
                  onChange={(e) => setAuthPasswordConfirm(e.target.value)}
                  required
                />
              )}
            </DialogContent>
            <DialogActions style={{ flexDirection: "column", padding: "16px 24px" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={authLoading}
                style={{ fontWeight: 700, marginBottom: "12px" }}
              >
                {authLoading ? <CircularProgress size={20} color="inherit" /> : isLoginView ? "ENTRAR E CONCLUIR" : "CRIAR CONTA E CONCLUIR"}
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  setIsLoginView(!isLoginView);
                  setAuthError("");
                }}
                style={{ textTransform: "none" }}
              >
                {isLoginView ? "Não possui conta? Cadastre-se" : "Já possui conta? Faça Login"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Email Verification Required Dialog */}
        <Dialog
          open={showEmailVerificationModal}
          onClose={() => { }}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: { borderRadius: "16px", padding: "16px 8px", textAlign: "center" }
          }}
        >
          <DialogContent>
            {(() => {
              const userEmail = auth.currentUser?.email || currentUser?.email || authEmail;
              const webmail = getWebmailInfo(userEmail);

              if (emailVerifiedSuccess) {
                return (
                  <Box textAlign="center" py={3}>
                    <CheckCircleIcon style={{ fontSize: 64, color: "#22C55E", marginBottom: 12 }} />
                    <Typography variant="h6" style={{ fontWeight: 800, color: "#166534", marginBottom: 6 }}>
                      E-mail Confirmado com Sucesso!
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Liberando seu acesso e preparando seu ambiente...
                    </Typography>
                  </Box>
                );
              }

              return (
                <>
                  <Box display="flex" justifyContent="center" mb={2}>
                    <div style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#EEF2FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#4F46E5"
                    }}>
                      <MailOutlineIcon style={{ fontSize: 36 }} />
                    </div>
                  </Box>

                  <Typography variant="h6" style={{ fontWeight: 800, color: "#1E293B", marginBottom: 8 }}>
                    Confirme seu E-mail
                  </Typography>

                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Enviamos um link de confirmação para:
                    <br />
                    <strong style={{ color: "#0F172A", wordBreak: "break-all" }}>
                      {userEmail}
                    </strong>
                  </Typography>

                  <Box display="flex" justifyContent="center" mb={2}>
                    <div className="email-pulse-indicator">
                      <span className="pulse-dot" />
                      <span>Aguardando confirmação em tempo real...</span>
                    </div>
                  </Box>

                  {webmail && (
                    <Box mb={2}>
                      <Button
                        variant="outlined"
                        fullWidth
                        className="webmail-btn"
                        onClick={() => window.open(webmail.url, "_blank")}
                      >
                        ✉️ {webmail.name}
                      </Button>
                    </Box>
                  )}

                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 20, backgroundColor: "#F8FAFC", padding: "10px", borderRadius: "8px" }}>
                    💡 Clique no link enviado na sua caixa de entrada (ou spam). Esta tela avançará automaticamente!
                  </Typography>

                  {verificationError && (
                    <Box mb={2} p={1.5} bgcolor="#FEF2F2" borderRadius={8} border="1px solid #FCA5A5">
                      <Typography variant="body2" color="error" style={{ fontSize: "13px", fontWeight: 500 }}>
                        {verificationError}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleCheckEmailVerified}
                    disabled={verifyingEmail}
                    style={{ fontWeight: 700, borderRadius: 8, padding: "12px", textTransform: "none", fontSize: "15px" }}
                  >
                    {verifyingEmail ? <CircularProgress size={22} color="inherit" /> : "Já verifiquei meu e-mail"}
                  </Button>

                  <Box mt={2} display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={handleResendEmail}
                      disabled={resendCooldown > 0}
                      style={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {resendCooldown > 0
                        ? `Reenviar e-mail (${resendCooldown}s)`
                        : "Reenviar e-mail de confirmação"}
                    </Button>

                    <Button
                      variant="text"
                      color="secondary"
                      size="small"
                      onClick={async () => {
                        setShowEmailVerificationModal(false);
                        setCredencials(null);
                        await logout();
                        setShowAuthModal(true);
                      }}
                      style={{ textTransform: "none", color: "#64748B" }}
                    >
                      Usar outro e-mail
                    </Button>
                  </Box>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Subscription Checkout Modal for Plan Acquisition */}
        <SubscriptionCheckoutModal
          open={showCheckoutModal}
          onClose={() => setShowCheckoutModal(false)}
          accountId={createdAccountId}
          initialPlanId={selectedPlan}
          initialAddonIds={selectedAddonIds}
          initialData={{
            companyName: businessForm.name,
            pseudonym: businessForm.name,
            responsibleName: businessForm.name,
            cpfCnpj: businessForm.cpf_cnpj,
            whatsapp: businessForm.whatsapp,
          }}
        />
      </Container>
    </div>
  );
}
